import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { string, arrayOf, number, func, object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import detectIt from 'detect-it';
import styled from 'styled-components/macro';
import delve from 'dlv';

import CountryList from 'components/styled/CountryList/CountryList';
import { CloseButton } from 'components/Header/EntityCompareHeader/CompareSearch/CompareSearch.style';

import { boxShadow, helperText } from 'styles/base-classes';

import { ReactComponent as IconClose } from 'icons/icon-close.svg';

import { CountryItem, StyledCompareBullet } from '../CountryCompareHeader';

import isChildOf from 'helpers/isChildOf';
import { withEntityContext } from 'context/entityContext';
import { selectChartsCustomers, loadCustomers } from 'redux/reducers/customers';
import { shouldLoadCharts, loadCharts } from 'redux/reducers/charts';
import { loadCountries } from 'redux/reducers/countries';

const CountrySearchField = styled('input')`
  flex: 1 1 auto;
  font: inherit;
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  outline: none;
  color: inherit;
  min-width: 0;
  text-overflow: ellipsis;

  &::placeholder {
    color: rgba(255, 255, 255, 0.75);
  }
`;

const Relative = styled('div')`
  position: relative;
`;

const CountryListWrapper = styled('div')`
  padding-top: 1.5em;
  max-height: 12em;
  overflow-y: ${({ smoothScrolling }) => (smoothScrolling ? 'scroll' : 'auto')};
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
  background: white;

  @media screen and (min-width: 30em) and (min-height: 50em) {
    max-height: 22.5em;
  }

  @media screen and (min-width: 30em) {
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    z-index: 1;
    ${boxShadow};
  }
`;

const HelperText = styled('p')`
  ${helperText};
  padding: 0 ${({ theme }) => theme.spacing.small};
`;

const { hasTouch, hasMouse } = detectIt;
const smoothScrolling = hasTouch && !hasMouse;

function addSupportedCountriesToChartsCustomers(chartsCustomers, charts) {
  if (chartsCustomers) {
    return chartsCustomers.map((customer) => {
      const customerCharts = charts.filter(
        (chart) => chart.customerId === customer.id
      );

      let supportedCountryIds = [];
      // eslint-disable-next-line no-unused-vars
      for (const chart of customerCharts) {
        supportedCountryIds.push(...chart.supportedCountryIds);
      }

      supportedCountryIds = Array.from(new Set(supportedCountryIds));

      return {
        ...customer,
        supportedCountryIds: supportedCountryIds,
      };
    });
  }

  return [];
}

/** Search field to select a country for country compare. */
function CountryCompareSearch(props) {
  const { index, countryIds, handleClose, handleChange, filters } = props;

  const [searchValue, setSearchValue] = useState('');
  const [countryListVisible, setCountryListVisible] = useState(true);

  const handleClickOutside = useCallback(
    (evt) => {
      if (!isChildOf(evt.target, 'countrySearch') && countryIds.length > 0) {
        setCountryListVisible(false);
      }
    },
    [countryIds]
  );

  const searchFieldRef = useRef(null);

  const updateSearchValue = useCallback((evt) => {
    setSearchValue(evt.target.value);
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    root?.addEventListener('click', handleClickOutside);

    return function onUnmount() {
      root?.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    searchFieldRef.current.focus();
  }, [searchFieldRef]);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadCustomers());
    dispatch(loadCountries());

    if (dispatch(shouldLoadCharts())) {
      dispatch(loadCharts());
    }
  }, [dispatch]);

  const countries = useSelector((state) => state.countries.data);

  const chartCustomers = useSelector((state) => {
    return addSupportedCountriesToChartsCustomers(
      selectChartsCustomers(state),
      delve(state, 'charts.data.charts', [])
    );
  });

  const filteredCountries = useMemo(
    () =>
      countries.reduce((acc, country) => {
        /** Match ID and DisplayName */
        if (
          (country.id.toLowerCase().includes(searchValue.toLowerCase()) ||
            country.displayName
              .toLowerCase()
              .includes(searchValue.toLowerCase())) &&
          /** Remove items that are already selected */
          !countryIds.includes(country.id)
        ) {
          const chartCustomer =
            (chartCustomers.length >= 1 &&
              chartCustomers.find(
                (chartCustomer) => String(chartCustomer.id) === filters.charts
              )) ||
            {};

          const supportedCountryIds = delve(
            chartCustomer,
            'supportedCountryIds',
            []
          );

          const isDisabled =
            filters.charts &&
            supportedCountryIds.length > 0 &&
            !supportedCountryIds.includes(country.id);

          /** Return a country with a isDisabled key assigned. */
          acc.push({
            ...country,
            isDisabled,
          });
        }

        return acc;
      }, []),
    [chartCustomers, countries, countryIds, filters.charts, searchValue]
  );

  return (
    <Relative data-country-search>
      <CountryItem>
        <StyledCompareBullet index={index} />
        <CountrySearchField
          value={searchValue}
          onChange={updateSearchValue}
          onFocus={() => setCountryListVisible(true)}
          ref={searchFieldRef}
          placeholder="Search country"
        />

        {countryIds.length > 0 && (
          <CloseButton onClick={handleClose}>
            <IconClose />
          </CloseButton>
        )}
      </CountryItem>
      {countryListVisible && (
        <CountryListWrapper smoothScrolling={smoothScrolling}>
          <CountryList
            countries={filteredCountries}
            handleChange={handleChange}
          />

          {searchValue.length > 0 && (
            <HelperText>
              Didn&apos;t find the country you were looking for? Possibly
              it&apos;s grouped in &quot;Other&quot;
            </HelperText>
          )}
        </CountryListWrapper>
      )}
    </Relative>
  );
}

CountryCompareSearch.propTypes = {
  /** The sequence number of the searchField used to determine the comparebullet color */
  index: number.isRequired,
  /** The countryIds of the countries that are being compared */
  countryIds: arrayOf(string).isRequired,
  /** Method to close the searchfield */
  handleClose: func.isRequired,
  /** Method that is fired when a country is selected */
  handleChange: func.isRequired,
  /** Currently active filters */
  filters: object,
};

export default withEntityContext(CountryCompareSearch);
